(() => {
  'use strict';

  const CARD_SELECTOR =
    'product-card.product-card-wrapper';

  const PRODUCT_LINK_SELECTOR =
    '.card__heading a[href*="/products/"]';

  const CARD_IMAGE_SELECTOR =
    '.card__media .card__image';


  const GALLERY_EXCLUDED_SECTION_SELECTOR =
    '.section-featured-collection, .section-related-products';


  const CARD_INTERACTIVE_SELECTOR =
    'a, button, input, select, textarea, label, summary, details, form, [role="button"], [role="link"], [contenteditable="true"], .nyigen-card-gallery-nav';


  /* ================================================================
     IMAGE SLIDE SPEED

     Previous: 440ms
     New: 700ms
     ================================================================ */

  const SLIDE_DURATION =
    700;


  const PRODUCT_IMAGE_WIDTH =
    900;


  const productCache =
    new Map();


  const imagePreloadCache =
    new Map();


  /* ================================================================
     PRODUCT HANDLE
     ================================================================ */

  const getHandle = (href) => {

    try {

      const url =
        new URL(
          href,
          window.location.origin
        );


      const match =
        url.pathname.match(
          /\/products\/([^/?#]+)/
        );


      return match
        ? decodeURIComponent(match[1])
        : null;

    } catch (error) {

      return null;
    }
  };


  /* ================================================================
     IMAGE URL
     ================================================================ */

  const normalizeImageUrl = (
    value
  ) => {

    if (!value) {

      return null;
    }


    if (
      typeof value === 'string'
    ) {

      return value;
    }


    if (
      typeof value === 'object'
    ) {

      return (
        value.src
        ||
        value.url
        ||
        value.preview_image?.src
        ||
        value.previewImage?.url
        ||
        null
      );
    }


    return null;
  };


  /* ================================================================
     IMAGE WIDTH
     ================================================================ */

  const addImageWidth = (
    src,
    width = PRODUCT_IMAGE_WIDTH
  ) => {

    if (!src) {

      return src;
    }


    try {

      const url =
        new URL(
          src,
          window.location.origin
        );


      url.searchParams.set(
        'width',
        String(width)
      );


      return url.toString();

    } catch (error) {

      return src;
    }
  };


  /* ================================================================
     LOAD PRODUCT IMAGES
     ================================================================ */

  const getProductImages =
    async (handle) => {

      if (
        productCache.has(handle)
      ) {

        return productCache.get(
          handle
        );
      }


      const request =
        (async () => {

          const root =
            window.Shopify?.routes?.root
            || '/';


          const endpoint =
            `${root}products/${encodeURIComponent(handle)}.js`;


          const response =
            await fetch(
              endpoint,
              {
                credentials:
                  'same-origin',

                headers: {
                  Accept:
                    'application/json'
                }
              }
            );


          if (!response.ok) {

            throw new Error(
              `Product request failed: ${response.status}`
            );
          }


          const product =
            await response.json();


          const imageUrls =
            [];


          if (
            Array.isArray(
              product.images
            )
          ) {

            product.images.forEach(
              (image) => {

                const src =
                  normalizeImageUrl(
                    image
                  );


                if (src) {

                  imageUrls.push(
                    src
                  );
                }
              }
            );
          }


          if (
            !imageUrls.length
            &&
            Array.isArray(
              product.media
            )
          ) {

            product.media.forEach(
              (media) => {

                const type =
                  media.media_type
                  ||
                  media.mediaType;


                if (
                  type
                  &&
                  type !== 'image'
                ) {

                  return;
                }


                const src =
                  normalizeImageUrl(
                    media
                  );


                if (src) {

                  imageUrls.push(
                    src
                  );
                }
              }
            );
          }


          return [
            ...new Set(
              imageUrls
            )
          ];

        })();


      productCache.set(
        handle,
        request
      );


      return request;
    };


  /* ================================================================
     PRELOAD IMAGE
     ================================================================ */

  const preloadImage = (
    src
  ) => {

    if (!src) {

      return Promise.resolve();
    }


    if (
      imagePreloadCache.has(src)
    ) {

      return imagePreloadCache.get(
        src
      );
    }


    const request =
      new Promise(
        (resolve) => {

          const image =
            new Image();


          image.decoding =
            'async';


          image.onload =
            resolve;


          image.onerror =
            resolve;


          image.src =
            src;


          if (
            image.complete
          ) {

            resolve();
          }
        }
      );


    imagePreloadCache.set(
      src,
      request
    );


    return request;
  };


  /* ================================================================
     ARROW ICON
     ================================================================ */

  const createChevron = (
    direction
  ) => {

    const namespace =
      'http://www.w3.org/2000/svg';


    const svg =
      document.createElementNS(
        namespace,
        'svg'
      );


    const path =
      document.createElementNS(
        namespace,
        'path'
      );


    svg.setAttribute(
      'viewBox',
      '0 0 24 24'
    );


    svg.setAttribute(
      'aria-hidden',
      'true'
    );


    path.setAttribute(
      'd',

      direction === 'prev'

        ? 'M14.5 5.5 8 12l6.5 6.5'

        : 'M9.5 5.5 16 12l-6.5 6.5'
    );


    svg.appendChild(
      path
    );


    return svg;
  };


  /* ================================================================
     NAVIGATION
     ================================================================ */

  const createNavigation = () => {

    const nav =
      document.createElement(
        'div'
      );


    nav.className =
      'nyigen-card-gallery-nav';


    const prev =
      document.createElement(
        'button'
      );


    prev.type =
      'button';


    prev.className =
      'nyigen-card-gallery-button nyigen-card-gallery-button--prev';


    prev.setAttribute(
      'aria-label',
      'Forrige produktbillede'
    );


    prev.appendChild(
      createChevron(
        'prev'
      )
    );


    const next =
      document.createElement(
        'button'
      );


    next.type =
      'button';


    next.className =
      'nyigen-card-gallery-button nyigen-card-gallery-button--next';


    next.setAttribute(
      'aria-label',
      'Næste produktbillede'
    );


    next.appendChild(
      createChevron(
        'next'
      )
    );


    nav.append(
      prev,
      next
    );


    return {
      nav,
      prev,
      next
    };
  };


  /* ================================================================
     WAIT FOR SLIDE
     ================================================================ */

  const waitForSlide = (
    element
  ) => {

    return new Promise(
      (resolve) => {

        let finished =
          false;


        const finish =
          () => {

            if (finished) {

              return;
            }


            finished =
              true;


            element.removeEventListener(
              'transitionend',
              onTransitionEnd
            );


            resolve();
          };


        const onTransitionEnd =
          (event) => {

            if (
              event.target !== element
              ||
              event.propertyName !== 'translate'
            ) {

              return;
            }


            finish();
          };


        element.addEventListener(
          'transitionend',
          onTransitionEnd
        );


        window.setTimeout(
          finish,
          SLIDE_DURATION + 150
        );
      }
    );
  };


  /* ================================================================
     WHOLE CARD LINK

     The title link remains the canonical product URL.
     Clicking any non-interactive area of the card opens that same product.
     Dragging a carousel does not trigger navigation.
     ================================================================ */

  const enableWholeCardLink = (
    wrapper
  ) => {

    if (
      !(wrapper instanceof HTMLElement)
      ||
      wrapper.dataset
        .nyigenCardLinkInitialized
      === 'true'
    ) {

      return;
    }


    const productLink =
      wrapper.querySelector(
        PRODUCT_LINK_SELECTOR
      );


    if (!productLink) {

      return;
    }


    wrapper.dataset
      .nyigenCardLinkInitialized =
      'true';


    let pointerStartX =
      0;


    let pointerStartY =
      0;


    let pointerMoved =
      false;


    wrapper.addEventListener(
      'pointerdown',
      (event) => {

        if (
          event.button !== undefined
          &&
          event.button !== 0
        ) {

          return;
        }


        pointerStartX =
          event.clientX;


        pointerStartY =
          event.clientY;


        pointerMoved =
          false;
      },
      {
        passive: true
      }
    );


    wrapper.addEventListener(
      'pointermove',
      (event) => {

        if (pointerMoved) {

          return;
        }


        const distanceX =
          Math.abs(
            event.clientX
            -
            pointerStartX
          );


        const distanceY =
          Math.abs(
            event.clientY
            -
            pointerStartY
          );


        if (
          distanceX > 8
          ||
          distanceY > 8
        ) {

          pointerMoved =
            true;
        }
      },
      {
        passive: true
      }
    );


    wrapper.addEventListener(
      'pointercancel',
      () => {

        pointerMoved =
          true;
      },
      {
        passive: true
      }
    );


    wrapper.addEventListener(
      'click',
      (event) => {

        if (
          event.defaultPrevented
          ||
          pointerMoved
        ) {

          pointerMoved =
            false;

          return;
        }


        const target =
          event.target;


        if (
          !(target instanceof Element)
          ||
          target.closest(
            CARD_INTERACTIVE_SELECTOR
          )
        ) {

          return;
        }


        window.location.assign(
          productLink.href
        );
      }
    );
  };


  /* ================================================================
     INITIALIZE CARD
     ================================================================ */

  const initializeCard = (
    wrapper
  ) => {

    if (
      !(wrapper instanceof HTMLElement)
    ) {

      return;
    }


    enableWholeCardLink(
      wrapper
    );


    if (
      wrapper.closest(
        GALLERY_EXCLUDED_SECTION_SELECTOR
      )
    ) {

      return;
    }


    if (
      wrapper.dataset
        .nyigenGalleryInitialized
      === 'true'
    ) {

      return;
    }


    wrapper.dataset
      .nyigenGalleryInitialized =
      'true';


    const card =
      wrapper.querySelector(
        ':scope > .product-card.card'
      );


    const cardInner =
      card?.querySelector(
        ':scope > .card__inner'
      );


    const cardImage =
      cardInner?.querySelector(
        CARD_IMAGE_SELECTOR
      );


    const productLink =
      card?.querySelector(
        PRODUCT_LINK_SELECTOR
      );


    if (
      !card
      ||
      !cardInner
      ||
      !cardImage
      ||
      !productLink
    ) {

      return;
    }


    if (
      card.classList.contains(
        'card--horizontal'
      )
    ) {

      return;
    }


    const media =
      cardImage.closest(
        '.media'
      );


    if (!media) {

      return;
    }


    const handle =
      getHandle(
        productLink.href
      );


    if (!handle) {

      return;
    }


    let images =
      [];


    let currentIndex =
      0;


    let loading =
      false;


    let ready =
      false;


    let animating =
      false;


    let navElements =
      null;


    /* ================================================================
       UPDATE ARROWS
       ================================================================ */

    const updateNavigation =
      () => {

        if (!navElements) {

          return;
        }


        navElements.prev.hidden =
          currentIndex <= 0;


        navElements.next.hidden =
          currentIndex >=
          images.length - 1;
      };


    /* ================================================================
       PRELOAD NEXT / PREVIOUS
       ================================================================ */

    const preloadAdjacent =
      () => {

        const previous =
          images[
            currentIndex - 1
          ];


        const next =
          images[
            currentIndex + 1
          ];


        if (previous) {

          preloadImage(
            addImageWidth(
              previous
            )
          );
        }


        if (next) {

          preloadImage(
            addImageWidth(
              next
            )
          );
        }
      };


    /* ================================================================
       SLOW PREMIUM IMAGE SLIDE
       ================================================================ */

    const goToImage =
      async (
        targetIndex
      ) => {

        if (animating) {

          return;
        }


        if (
          targetIndex < 0
          ||
          targetIndex >= images.length
          ||
          targetIndex === currentIndex
        ) {

          return;
        }


        animating =
          true;


        navElements.nav
          .classList.add(
            'is-animating'
          );


        const direction =
          targetIndex >
          currentIndex
            ? 1
            : -1;


        const targetSrc =
          addImageWidth(
            images[targetIndex]
          );


        await preloadImage(
          targetSrc
        );


        const incomingImage =
          cardImage.cloneNode(
            false
          );


        incomingImage.removeAttribute(
          'srcset'
        );


        incomingImage.removeAttribute(
          'sizes'
        );


        incomingImage.removeAttribute(
          'loading'
        );


        incomingImage.removeAttribute(
          'fetchpriority'
        );


        incomingImage.alt =
          '';


        incomingImage.classList.add(
          'nyigen-card-gallery-slide-image'
        );


        incomingImage.src =
          targetSrc;


        const incomingStart =
          direction === 1
            ? 100
            : -100;


        const currentEnd =
          direction === 1
            ? -100
            : 100;


        cardImage.classList.add(
          'nyigen-card-gallery-active-image'
        );


        cardImage.style.translate =
          '0 0';


        incomingImage.style.translate =
          `${incomingStart}% 0`;


        media.appendChild(
          incomingImage
        );


        void incomingImage.offsetWidth;


        requestAnimationFrame(
          () => {

            requestAnimationFrame(
              () => {

                cardImage.style.translate =
                  `${currentEnd}% 0`;


                incomingImage.style.translate =
                  '0 0';
              }
            );
          }
        );


        await waitForSlide(
          incomingImage
        );


        cardImage.classList.add(
          'nyigen-card-gallery-no-transition'
        );


        cardImage.removeAttribute(
          'srcset'
        );


        cardImage.removeAttribute(
          'sizes'
        );


        cardImage.src =
          targetSrc;


        cardImage.style.translate =
          '0 0';


        currentIndex =
          targetIndex;


        updateNavigation();


        requestAnimationFrame(
          () => {

            incomingImage.remove();


            cardImage.classList.remove(
              'nyigen-card-gallery-no-transition'
            );


            cardImage.classList.remove(
              'nyigen-card-gallery-active-image'
            );


            cardImage.style.translate =
              '';


            navElements.nav
              .classList.remove(
                'is-animating'
              );


            animating =
              false;


            preloadAdjacent();
          }
        );
      };


    /* ================================================================
       BUTTON EVENTS
       ================================================================ */

    const stopCardLink = (
      event
    ) => {

      event.preventDefault();

      event.stopPropagation();
    };


    const buildGallery =
      async () => {

        if (
          ready
          ||
          loading
        ) {

          return;
        }


        loading =
          true;


        try {

          images =
            await getProductImages(
              handle
            );


          if (
            images.length < 2
          ) {

            return;
          }


          const currentSrc =
            cardImage.currentSrc
            ||
            cardImage.src;


          let currentPath =
            '';


          try {

            currentPath =
              new URL(
                currentSrc,
                window.location.origin
              ).pathname;

          } catch (error) {

            currentPath =
              '';
          }


          const matchingIndex =
            images.findIndex(
              (src) => {

                try {

                  return (
                    new URL(
                      src,
                      window.location.origin
                    ).pathname
                    ===
                    currentPath
                  );

                } catch (error) {

                  return false;
                }
              }
            );


          currentIndex =
            matchingIndex >= 0
              ? matchingIndex
              : 0;


          navElements =
            createNavigation();


          cardInner.appendChild(
            navElements.nav
          );


          navElements.prev
            .addEventListener(
              'pointerdown',
              stopCardLink
            );


          navElements.next
            .addEventListener(
              'pointerdown',
              stopCardLink
            );


          navElements.prev
            .addEventListener(
              'click',
              (event) => {

                stopCardLink(
                  event
                );


                goToImage(
                  currentIndex - 1
                );
              }
            );


          navElements.next
            .addEventListener(
              'click',
              (event) => {

                stopCardLink(
                  event
                );


                goToImage(
                  currentIndex + 1
                );
              }
            );


          wrapper.classList.add(
            'nyigen-card-gallery-enabled'
          );


          navElements.nav
            .classList.add(
              'is-ready'
            );


          updateNavigation();


          preloadAdjacent();


          ready =
            true;

        } catch (error) {

          console.warn(
            '[Nyigen product card gallery]',
            error
          );

        } finally {

          loading =
            false;
        }
      };


    wrapper.addEventListener(
      'pointerenter',
      buildGallery,
      {
        passive: true
      }
    );


    wrapper.addEventListener(
      'focusin',
      buildGallery
    );
  };


  /* ================================================================
     INITIALIZE ALL
     ================================================================ */

  const initializeAll = (
    root = document
  ) => {

    if (
      root.matches?.(
        CARD_SELECTOR
      )
    ) {

      initializeCard(
        root
      );
    }


    root.querySelectorAll?.(
      CARD_SELECTOR
    ).forEach(
      initializeCard
    );
  };


  /* ================================================================
     START
     ================================================================ */

  const start = () => {

    initializeAll(
      document
    );


    const observer =
      new MutationObserver(
        (mutations) => {

          mutations.forEach(
            (mutation) => {

              mutation.addedNodes.forEach(
                (node) => {

                  if (
                    node instanceof HTMLElement
                  ) {

                    initializeAll(
                      node
                    );
                  }
                }
              );
            }
          );
        }
      );


    observer.observe(
      document.documentElement,
      {
        childList: true,
        subtree: true
      }
    );


    document.addEventListener(
      'shopify:section:load',
      (event) => {

        if (
          event.target
          instanceof HTMLElement
        ) {

          initializeAll(
            event.target
          );
        }
      }
    );
  };


  if (
    document.readyState
    === 'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      start,
      {
        once: true
      }
    );

  } else {

    start();
  }

})();